import { useState, useEffect } from 'react';

/**
 * Custom hook to detect and track dark mode state
 *
 * This hook automatically detects when dark mode is toggled and updates
 * the returned value accordingly. It listens for changes to the 'dark'
 * class on the document element.
 *
 * @returns boolean indicating whether dark mode is currently active
 *
 * @example
 * ```tsx
 * import { useDarkMode } from '@/lib/hooks';
 *
 * function MyComponent() {
 *   const isDarkMode = useDarkMode();
 *
 *   return (
 *     <div className={isDarkMode ? 'dark-styles' : 'light-styles'}>
 *       Current theme: {isDarkMode ? 'Dark' : 'Light'}
 *     </div>
 *   );
 * }
 * ```
 */
export function useDarkMode(): boolean {
    const [isDarkMode, setIsDarkMode] = useState(document.documentElement.classList.contains('dark'));

    useEffect(() => {
        // Check for dark mode on mount and listen for changes
        const checkDarkMode = () => {
            setIsDarkMode(document.documentElement.classList.contains('dark'));
        };

        checkDarkMode();

        // Listen for class changes on the document element
        const observer = new MutationObserver(checkDarkMode);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });

        return () => observer.disconnect();
    }, []);

    return isDarkMode;
}
